import { useEffect, useRef } from 'react';
import {
  OptiSignsBridge,
  CHILD_ACTION,
  setActiveBridge,
  type SyncThemePayload,
} from './OptiSignsBridge';
import { useEmbedStore } from '@/stores/embed-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useTheme } from '@/contexts/theme-context';
import { auth } from '@/lib/firebase';
import { posthog } from '@/lib/analytics/posthog';

const API_URL = import.meta.env.VITE_API_URL;

// OptiSigns role codes that may use OptiDev: 0=owner, 1=admin, 8=superadmin.
// Anything else (regular user, viewer, etc.) sees the "ask your admin" modal
// when they try to provision via the EmbedOnboarding "Build it" CTA.
const ALLOWED_OPTISIGNS_ROLES = ['0', '1', '8'];

/**
 * Read a Firebase ID token's JWT payload (base64url) WITHOUT verifying the
 * signature. We use this only to pull the user's OWN email/name to enrich the
 * PostHog person profile — the email claim is already present on the token the
 * parent hands us, so no backend lookup is needed. Never use this for
 * authorization decisions (the backend always re-verifies via verifyIdToken).
 * Returns null on any malformed input.
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    // Percent-decode so non-ASCII names survive the UTF-8 → atob round-trip.
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Sync the current embed user with OptiDev backend.
 *
 * Only call this when a workspaceId is in hand — either from the parent's
 * ON_INIT (returning admin user with linked workspace, or after scio3
 * auto-provisioned) or from PROVISION_WORKSPACE (in-iframe Start Trial flow).
 *
 * Calling without a workspaceId would route into auth.service.ts's JIT branch
 * (lines ~390-437), which auto-creates a "personal workspace" — an unwanted
 * side effect that pollutes the OptiDev DB. Provisioning of new workspaces is
 * the responsibility of `POST /api/workspaces/provision` (called by OptiPrime
 * via the scio3 PROVISION_WORKSPACE flow); that path creates user + workspace
 * + membership atomically with proper source metadata.
 *
 * Exported so EmbedOnboarding can call it after PROVISION_WORKSPACE to populate
 * AuthContext before clearing needsProvisioning.
 *
 * Returns:
 *   true  — AuthContext is populated (embed-auth event dispatched with user)
 *           OR the user was correctly identified as needs_admin.
 *   false — anything went wrong (no token, network error, non-2xx response,
 *           or response had no user field). Callers downstream of provisioning
 *           MUST check the return and abort the "clear needsProvisioning + render
 *           Dashboard" sequence on false, otherwise Dashboard mounts with
 *           user=null and surfaces a half-broken UI.
 */
export async function syncEmbedUserWithBackend(workspaceId: string): Promise<boolean> {
  const token = useEmbedStore.getState().parentToken;
  if (!token) {
    console.error('[OptiSignsBridge] syncEmbedUserWithBackend: no parent token');
    return false;
  }
  try {
    const res = await fetch(`${API_URL}/api/auth/join`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      // No `role` passed: when workspaceId is provided AND the user already
      // exists (true after provisionWorkspace's _findOrCreateUser), syncUser
      // bypasses the role gate and embed/JIT branches and just returns user
      // info. For first-time admin via ON_INIT (Path A), the embed branch
      // creates the user record linked to the existing workspace.
      body: JSON.stringify({ source: 'optisigns', workspaceId }),
    });
    if (!res.ok) {
      console.error('[OptiSignsBridge] /api/auth/join failed:', res.status);
      return false;
    }
    const data = await res.json();
    if (data.needs_admin) {
      // Backend determined the role isn't permitted. EmbedOnboarding will see
      // needsAdminAccess=true and route to the "ask your admin" modal on click.
      useEmbedStore.getState().setNeedsAdminAccess(true);
      return true;
    }
    if (data.user) {
      auth.currentUser = data.user;
      window.dispatchEvent(new CustomEvent('embed-auth', { detail: data.user }));
      // Link the OptiDev user id to the current scio-uid distinct_id without
      // switching identities — keeps person properties intact for subsequent events.
      try {
        posthog.alias(data.user.id);
      } catch (err) {
        console.error('[OptiSignsBridge] PostHog alias failed:', err);
      }
      return true;
    }
    console.error('[OptiSignsBridge] /api/auth/join returned no user field:', data);
    return false;
  } catch (err) {
    console.error('[OptiSignsBridge] /api/auth/join error:', err);
    return false;
  }
}

/**
 * React hook that initializes the OptiSignsBridge when in embed mode.
 *
 * Uses the standard scio3 bridge protocol (same as Designer/scio1):
 * - Child → Parent: JSON.stringify({ action, requestId, data })
 * - Parent → Child: JSON.stringify({ action: 'responsePromise', requestId, data })
 * - Parent push:    JSON.stringify({ action, data })        (no requestId)
 *
 * Auth flow:
 * 1. ON_INIT → parent responds with { sessionId, teamId, teamName, theme, role, workspaceId }
 * 2. Decode sessionId → Firebase ID token; stash it in embed-store for later API calls.
 * 3. Role gating client-side: non-admin OptiSigns roles → setNeedsAdminAccess. The same
 *    EmbedOnboarding UI renders for both admins and non-admins; the "ask your admin"
 *    modal opens when a non-admin clicks "Build it".
 * 4. If parent provided a workspaceId → call syncEmbedUserWithBackend (workspace exists,
 *    user gets linked via the embed branch, no JIT side effect).
 *    If no workspaceId → skip the sync entirely; EmbedOnboarding renders, and after the
 *    user clicks "Build it" the PROVISION_WORKSPACE flow handles workspace+user creation
 *    via /api/workspaces/provision; EmbedOnboarding then calls syncEmbedUserWithBackend()
 *    to populate AuthContext before clearing needsProvisioning.
 *
 * Theme sync:
 * - ON_INIT response includes current resolved theme → applied immediately
 * - Parent pushes SYNC_THEME when user toggles theme → applied via setTheme
 */
export function useOptiSignsBridge(): OptiSignsBridge | null {
  const bridgeRef = useRef<OptiSignsBridge | null>(null);
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);
  const { setTheme } = useTheme();

  // Keep setTheme in a ref so the bridge handler always has the latest
  const setThemeRef = useRef(setTheme);
  setThemeRef.current = setTheme;

  useEffect(() => {
    if (!isEmbedded) return;

    // DEV-ONLY: test the embed onboarding UI without running scio3/open-api.
    // Fakes the ON_INIT result (industry + "no workspace yet") so
    // EmbedOnboarding renders with personalized content. Stripped from
    // production builds via the DEV gate. Examples (on localhost:3000):
    //   ?embed=optisigns&devIndustry=manufacturing   → manufacturing (live demo)
    //   ?embed=optisigns&devIndustry=k12_education    → resolves via alias → education
    //   ?embed=optisigns&devIndustry=itsm            → no content → generic default
    //   ?embed=optisigns&devIndustry=none             → industry null → generic default
    //   ?embed=optisigns&devIndustry=&devIndustrySecondary=retail → primary→secondary fallback
    // Note: "Build it" won't provision in this mode (no real parent bridge).
    if (import.meta.env.DEV) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('devIndustry') || params.has('devIndustrySecondary')) {
        const norm = (v: string | null): string | null =>
          v && v !== 'none' ? v : null;
        const primary = norm(params.get('devIndustry'));
        const secondary = norm(params.get('devIndustrySecondary'));
        useEmbedStore
          .getState()
          .setIndustry(
            primary || secondary ? { primary, secondary, reason: null } : null,
          );
        useEmbedStore.getState().setNeedsProvisioning(true);
        // eslint-disable-next-line no-console
        console.log('[OptiSignsBridge] DEV industry override:', { primary, secondary });
        return; // skip real bridge setup — there's no parent window to talk to
      }
    }

    const bridge = new OptiSignsBridge();
    bridgeRef.current = bridge;
    setActiveBridge(bridge);

    // Listen for SYNC_THEME pushes from parent
    bridge.on('SYNC_THEME', (data) => {
      const payload = data as SyncThemePayload | undefined;
      if (payload?.theme === 'light' || payload?.theme === 'dark') {
        setThemeRef.current(payload.theme);
      }
    });

    // Listen for WORKSPACE_READY push — scio3 sends this after provisioning succeeds.
    // Only update workspace store here. Do NOT clear needsProvisioning — that must
    // happen in EmbedOnboarding.handleCreate AFTER it writes the prompt to the URL
    // via history.replaceState. Clearing it here would unmount EmbedOnboarding before
    // the PROVISION_WORKSPACE response arrives, losing the prompt.
    bridge.on('WORKSPACE_READY', (data) => {
      const payload = data as { workspaceId?: string } | undefined;
      if (payload?.workspaceId) {
        useWorkspaceStore.getState().setActiveWorkspaceId(payload.workspaceId);
      }
    });

    // Start listening for parent messages
    bridge.init();

    // Send ON_INIT request — parent responds with { sessionId, teamId, teamName, theme }
    // Same pattern as SmallApp ON_INIT → sendTokenNew(requestId)
    (async () => {
      try {
        const response = await bridge.sendRequest<{
          sessionId?: string;
          teamId?: string;
          teamName?: string;
          workspaceId?: string;
          theme?: 'light' | 'dark';
          role?: string;
          industry?: { primary: string | null; secondary: string | null; reason: string | null } | null;
          onboarding?: {
            audience: 'internal' | 'external' | 'both' | null;
            useCases: string[];
            useCasesOther: string | null;
          } | null;
        }>(CHILD_ACTION.ON_INIT);

        // Apply theme from parent immediately
        if (response?.theme === 'light' || response?.theme === 'dark') {
          setThemeRef.current(response.theme);
        }

        // Industry classification from scio3 (for welcome-page customization). Fail-open if absent.
        if (response?.industry) {
          useEmbedStore.getState().setIndustry(response.industry);
          // eslint-disable-next-line no-console
          console.log('[OptiSignsBridge] Industry received from parent:', response.industry);
        } else {
          // eslint-disable-next-line no-console
          console.log('[OptiSignsBridge] No industry in ON_INIT response (fail-open → generic prompts)');
        }

        // Onboarding survey signal from scio3 (for welcome-page customization). Fail-open if absent.
        if (response?.onboarding) {
          useEmbedStore.getState().setOnboarding(response.onboarding);
          // eslint-disable-next-line no-console
          console.log('[OptiSignsBridge] Onboarding received from parent:', response.onboarding);
        } else {
          // eslint-disable-next-line no-console
          console.log('[OptiSignsBridge] No onboarding in ON_INIT response (fail-open → generic prompts)');
        }

        if (response?.sessionId) {
          // Decode sessionId: base64 → JSON { token, expires, isFBToken, uid }
          const decoded = JSON.parse(atob(response.sessionId));
          const token = decoded.token;

          // Stash token for later API calls (auth/me, /api/auth/join after PROVISION_WORKSPACE, etc.)
          useEmbedStore.getState().setParentToken(token);

          // Stash OptiSigns account context — attached to embed lead tracking
          // (/api/embed-visitors) so each lead is tied to account + role.
          useEmbedStore.getState().setScioContext({
            teamId: response.teamId ?? null,
            teamName: response.teamName ?? null,
            role: response.role ?? null,
          });

          // Early PostHog identify — attaches subsequent events to a real person profile.
          // Without this, events fire under an anonymous distinct_id until OptiDev backend
          // sync runs much later. PostHog aliases this scio uid to the OptiDev user id
          // when syncEmbedUserWithBackend later calls identify with the OptiDev user id.
          //
          // is_optidev_eligible: true iff role is one of the allowed admin roles.
          // The main funnel filters by this to exclude non-admins (who are blocked
          // from the trial path by design — they would inflate the build_clicked →
          // trial_confirmed drop-off otherwise).
          try {
            if (decoded.uid) {
              // Email/name come straight off the Firebase ID token claims — no
              // backend lookup needed. Without this, PostHog shows only the
              // opaque scio uid until the OptiDev DB sync runs post-trial.
              const claims = decodeJwtPayload(decoded.token);
              const email = typeof claims?.email === 'string' ? claims.email : null;
              const name = typeof claims?.name === 'string' ? claims.name : null;

              posthog.identify(decoded.uid, {
                // PostHog auto-recognizes `email`/`name` for person display,
                // search, and cohorts. Available from the very first event.
                email,
                name,
                scio_team_id: response.teamId,
                scio_team_name: response.teamName,
                scio_role: response.role,
                is_optidev_eligible: ALLOWED_OPTISIGNS_ROLES.includes(response.role ?? ''),
                // Industry (primary/secondary) as person properties so the
                // welcome-page funnel can be sliced by industry. `reason` is
                // internal context and never sent.
                industry_primary: response.industry?.primary ?? null,
                industry_secondary: response.industry?.secondary ?? null,
              });
            }
          } catch (err) {
            console.error('[OptiSignsBridge] PostHog identify failed:', err);
          }

          // Client-side role gate. Done here (not via backend) because we no longer
          // unconditionally call /api/auth/join on iframe load — we only call it when a
          // workspaceId exists. Non-admin users see the same EmbedOnboarding UI; the
          // "ask your admin" modal opens when they click "Build it".
          if (!ALLOWED_OPTISIGNS_ROLES.includes(response?.role || '')) {
            useEmbedStore.getState().setNeedsAdminAccess(true);
          }

          // Only sync the user with OptiDev backend when a workspace already exists.
          // Without workspaceId, /api/auth/join's JIT branch would auto-create a
          // junk personal workspace; provisioning a real one is the job of
          // PROVISION_WORKSPACE → /api/workspaces/provision (called from scio3).
          if (response?.workspaceId) {
            await syncEmbedUserWithBackend(response.workspaceId);
          }
        }

        // If parent reports no workspaceId, the account has no OptiDev workspace yet.
        // Show EmbedOnboarding so the admin can provision one (or non-admin sees the
        // same UI and the "ask your admin" modal on click).
        if (!response?.workspaceId) {
          useEmbedStore.getState().setNeedsProvisioning(true);
        }

        // Workspace ID is set from ?ws=<uuid> URL param (workspace-store.ts)
        // Validate it matches what parent sent
        const wsFromUrl = new URLSearchParams(window.location.search).get('ws');
        if (response?.workspaceId && response.workspaceId !== wsFromUrl) {
          console.warn('[OptiSignsBridge] Workspace mismatch — URL ws:', wsFromUrl, 'parent workspaceId:', response.workspaceId);
        }
      } catch (err) {
        console.error('[OptiSignsBridge] Init failed:', err);
      }
    })();

    return () => {
      bridge.destroy();
      bridgeRef.current = null;
      setActiveBridge(null);
    };
  }, [isEmbedded]);

  return bridgeRef.current;
}
